import { useCallback, useEffect } from 'react';
import capitalise from 'lodash/capitalize';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';

import { NsContent } from 'components/common/ns-content/NsContent';
import { NsRadioInput } from 'components/common/ns-radio-input/NsRadioInput';
import { ConsumptionText } from './ConsumptionText';
import { ConsumptionActualForm } from './ConsumptionActualForm';

import { setConsumptionType } from 'state/consumption-entry/consumption-entry-slice';
import {
  setConsumption,
  setInitialState as resetConsumption
} from 'state/consumption/consumption-slice';
import { selectFuelType } from 'state/fuel-type/fuel-type-slice';

import { useEnergyUsage } from 'hooks/useEnergyUsage';
import { usePartner } from 'components/app/routes/usePartner';
import { useHasInitialState } from '../hooks/useHasInitialState';
import { useConsumptionEntry } from '../hooks/useConsumptionEntry';

import { CONSUMPTION_TYPES } from 'config/app-config';

export const testId = 'consumptionEntryTestId';

const CONSUMPTION_TYPE_OPTIONS = [
  {
    label: capitalise(CONSUMPTION_TYPES.LOW),
    value: CONSUMPTION_TYPES.LOW
  },
  {
    label: capitalise(CONSUMPTION_TYPES.MEDIUM),
    value: CONSUMPTION_TYPES.MEDIUM
  },
  {
    label: capitalise(CONSUMPTION_TYPES.HIGH),
    value: CONSUMPTION_TYPES.HIGH
  }
];

export const ConsumptionEntry = () => {
  const dispatch = useDispatch();
  const selectedFuelType = useSelector(selectFuelType, shallowEqual);
  const getEnergyUsage = useEnergyUsage();
  const {
    consumptionType,
    isMoveInJourney,
    isActual,
    isEnergyUsageExceeds
  } = useConsumptionEntry();
  const {
    fuel: hasFuelInitialState,
    consumptionType: hasConsumptionTypeInitialState
  } = useHasInitialState();

  const { experimentDefaultConsumptionType } = useFlags();
  const { isDSJourney } = usePartner();

  const defaultConsumptionType =
    isDSJourney && experimentDefaultConsumptionType
      ? CONSUMPTION_TYPES.ACTUAL
      : CONSUMPTION_TYPES.MEDIUM;

  const handleChange = useCallback(
    (event) => {
      const {
        target: { value }
      } = event;

      dispatch(setConsumptionType(value));
      if (value === CONSUMPTION_TYPES.ACTUAL) {
        dispatch(resetConsumption());
      } else {
        const consolidatedUsage = getEnergyUsage(value, selectedFuelType);

        dispatch(setConsumption(consolidatedUsage));
      }
    },
    [dispatch, getEnergyUsage, selectedFuelType]
  );

  useEffect(() => {
    if (!hasFuelInitialState && hasConsumptionTypeInitialState) {
      handleChange({ target: { value: defaultConsumptionType } });
    }
  }, [
    hasFuelInitialState,
    hasConsumptionTypeInitialState,
    handleChange,
    defaultConsumptionType
  ]);

  return (
    <>
      <NsRadioInput
        className="inline"
        heading="How much energy do you use a year?"
        helper="Why get an accurate quote?"
        tipDetail="If you're comparing energy prices online, make sure you put in the actual energy you use. That way, you'll get the right quote and won't pay more than you need to."
        name="consumptionEntry"
        dataTestId="radioConsumptionEntrTestId"
        onChange={handleChange}
        value={consumptionType || defaultConsumptionType}
        options={[
          ...(isMoveInJourney
            ? []
            : [
                {
                  label: capitalise(CONSUMPTION_TYPES.ACTUAL),
                  value: CONSUMPTION_TYPES.ACTUAL
                }
              ]),
          ...CONSUMPTION_TYPE_OPTIONS
        ]}
      />
      <NsContent>
        <div>
          {!isActual && (
            <ConsumptionText
              consumptionType={consumptionType || CONSUMPTION_TYPES.MEDIUM}
              isMoveInJourney={isMoveInJourney}
            />
          )}
        </div>
      </NsContent>
      {isActual && (
        <ConsumptionActualForm isEnergyUsageExceeds={isEnergyUsageExceeds} />
      )}
    </>
  );
};

export default ConsumptionEntry;
